"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { appsForEmail } from "@/lib/apps";
import DebugPanel from "@/components/DebugPanel";

/**
 * Hu Family Apps — the launcher. Lists the apps this service hosts, filtered
 * to what the signed-in account can access. The "Open by default" choice
 * (lib/launch-preference — the one Home Screen install opens here and jumps
 * into the chosen app on a cold start) lives in the Debug panel below, so it
 * doesn't crowd the launcher.
 */
export default function Home() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if (status === "unauthenticated") router.replace("/login");
  }, [status, router]);

  if (status !== "authenticated") {
    return (
      <div className="flex min-h-dvh items-center justify-center text-sm" style={{ color: "var(--text3)" }}>
        Loading…
      </div>
    );
  }

  const apps = appsForEmail(session.user?.email);

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-6xl flex-col px-6 py-12">
      <header className="mx-auto mb-10 w-full max-w-lg text-center">
        <div className="text-4xl" aria-hidden>
          🏠
        </div>
        <h1 className="mt-3 text-3xl font-bold">Hu Family Apps</h1>
        <p className="mt-1" style={{ color: "var(--text3)" }}>
          {session.user?.name ?? session.user?.email}
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-lg flex-col gap-4">
        {apps.map((app) => (
          <Link
            key={app.id}
            href={app.href}
            className="flex items-center gap-4 rounded-2xl border p-5 shadow-sm transition-transform hover:-translate-y-0.5"
            style={{ background: "var(--card)", borderColor: "var(--border)" }}
          >
            <span className="text-3xl" aria-hidden>
              {app.emoji}
            </span>
            <span>
              <span className="block text-lg font-bold">{app.name}</span>
              <span className="block text-sm" style={{ color: "var(--text2)" }}>
                {app.tagline}
              </span>
            </span>
            <span className="ml-auto text-xl" style={{ color: "var(--text4)" }} aria-hidden>
              →
            </span>
          </Link>
        ))}
        {apps.length === 0 && (
          <p className="text-center text-sm" style={{ color: "var(--text3)" }}>
            Your account doesn’t have access to any apps yet.
          </p>
        )}
      </div>

      {showDebug && (
        // Debug tooling breaks out of the narrow launcher column so its fetch
        // log and cache tables have room on wide viewports (they're mobile-
        // portrait-cramped otherwise). Stays max-w-lg on phones.
        <div className="mx-auto mt-10 w-full max-w-lg lg:max-w-5xl">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text3)" }}>
            Debug · whole service
          </div>
          <DebugPanel apps={apps} />
        </div>
      )}

      <footer className="mx-auto mt-auto flex w-full max-w-lg items-center justify-center gap-4 pt-10 text-center">
        <button
          onClick={() => signOut({ callbackUrl: "/login" })}
          className="text-sm underline-offset-2 hover:underline"
          style={{ color: "var(--text3)" }}
        >
          Sign out
        </button>
        <span style={{ color: "var(--text4)" }}>·</span>
        <button
          onClick={() => setShowDebug((v) => !v)}
          className="text-sm underline-offset-2 hover:underline"
          style={{ color: "var(--text3)" }}
        >
          {showDebug ? "Hide debug" : "Debug"}
        </button>
      </footer>
    </div>
  );
}
